import { put, takeLatest, takeEvery, all } from 'redux-saga/effects';

import {
  listAvailability,
  getAvailability,
  listAvailabilityVenues,
  findAvailability,
  getDateRangeAvailability,
  getSpecificVenueAvailability,
} from '../../graphql/queries';
import * as ACTIONS from './AvailabilityAction';
import * as TYPES from './AvailabilityTypes';
import {
  callGraphqlWithToken,
  dispatchError,
  dispatchRedirectError,
  getVenuesByPage,
} from '../helpers';

import {
  generateEventAvailabilityRequestPayload,
  generateListSearchRequestPayload,
  generateMultipleAvailabilityRequestPayload,
  generateMultipleDateRangeAvailabilityRequestPayload,
  generateSpecificVenuesSearchRequestPayload,
} from './helpers';
import moment from 'moment';
import { ERROR_PAGE_TYPES } from '../../utils/constants/ErrorPageTypes';

export function* availabilityListRequestHandler(data) {
  try {
    const result = yield callGraphqlWithToken({
      query: listAvailability,
      variables: { ...data.payload },
    });
    yield put(
      ACTIONS.actionAvailabilityListSuccess(result.data.listAvailability)
    );
  } catch (e) {
    console.log(e);
    yield dispatchError(e.message);
    yield put(ACTIONS.actionAvailabilityListFail(e));
  }
}

export function* getAvailabilityRequestHandler(data) {
  try {
    const result = yield callGraphqlWithToken({
      query: getAvailability,
      variables: { ...data.payload },
    });
    yield put(
      ACTIONS.actionGetAvailabilitySuccess(result.data.getAvailability)
    );
  } catch (e) {
    console.log(e);
    yield dispatchError(e.message);
    yield put(ACTIONS.actionGetAvailabilityFail(e));
  }
}

function* getAvailabities(dates) {
  const results = yield all(
    dates.map((date) => {
      return callGraphqlWithToken({
        query: getAvailability,
        variables: { ...date },
      });
    })
  );
  return results.map((result) => result.data.getAvailability);
}

function* getDateRangeAvailabities(dates) {
  const results = yield all(
    dates.map((date) => {
      return callGraphqlWithToken({
        query: getDateRangeAvailability,
        variables: { ...date },
      });
    })
  );
  return {
    dateRangeAvailability: results
      .flatMap((result) => result.data.getDateRangeAvailability)
      .filter(Boolean),
    errors: results.map((result) => result.errors).filter(Boolean),
  };
}

function* getSpecificVenueAvailabilities(payloads) {
  try {
    const results = yield all(
      payloads.map((payload) => {
        return callGraphqlWithToken({
          query: getSpecificVenueAvailability,
          variables: { ...payload },
        });
      })
    );
    if (results[0].errors?.length > 0) {
      throw new Error(
        results[0].errors.map((error) => error.message).join(', ')
      );
    }
    return results.map((result) => result.data.getSpecificVenueAvailability);
  } catch (e) {
    console.log(e.message);
    yield dispatchRedirectError(e.message);
    yield put(
      ACTIONS.actionGetSpecificVenueAvailabilityFail(payloads[0].venueIds)
    );
    return [];
  }
}

export function* getMultipleAvailabilityRequestHandler(data) {
  try {
    const requestPayload = generateMultipleAvailabilityRequestPayload(
      data.payload
    );
    const listAvailability = yield getAvailabities(requestPayload);
    yield put(ACTIONS.actionGetMultipleAvailabilitiesSuccess(listAvailability));
  } catch (e) {
    console.log(e);
    yield dispatchError(e.message);
    yield put(ACTIONS.actionGetMultipleAvailabilitiesFail(e));
  }
}

export function* findAvailabilityRequestHandler(data) {
  try {
    const result = yield callGraphqlWithToken({
      query: findAvailability,
      variables: { ...data.payload },
    });
    yield put(
      ACTIONS.actionFindAvailabilitySuccess(result.data.findAvailability)
    );
  } catch (e) {
    console.log(e);
    yield dispatchError(e.message);
    yield put(ACTIONS.actionFindAvailabilityFail(e));
  }
}

export function* loadDateAvailabilityRequest(data) {
  try {
    const result = yield callGraphqlWithToken({
      query: listAvailability,
      variables: { ...data.payload },
    });
    yield put(
      ACTIONS.actionLoadDateAvailabilitySuccess(result.data.listAvailability)
    );
  } catch (e) {
    console.log(e);
    yield dispatchError(e.message);
    yield put(ACTIONS.actionLoadDateAvailabilityFail);
  }
}

export function* getDateRangeAvailabilityHandler(data) {
  try {
    const requestPayload = generateMultipleDateRangeAvailabilityRequestPayload(
      data.payload
    );
    const { dateRangeAvailability, errors } = yield getDateRangeAvailabities(
      requestPayload
    );
    if (dateRangeAvailability?.length === 0 && errors?.length > 0) {
      throw new Error(ERROR_PAGE_TYPES.DATE_RANGE_ERROR_AVAILABILITY_VENUE_DETAILS_PAGE.value);
    }
    yield put(
      ACTIONS.actionGetDateRangeAvailabilitySuccess(dateRangeAvailability)
    );
  } catch (e) {
    console.log(e);
    yield dispatchRedirectError(e.message);
    yield put(ACTIONS.actionGetDateRangeAvailabilityFail(e.message));
  }
}

export function* getPreloadDateRangeAvailabilityHandler(data) {
  try {
    const requestPayload = generateMultipleDateRangeAvailabilityRequestPayload(
      data.payload
    );
    const { dateRangeAvailability } = yield getDateRangeAvailabities(
      requestPayload
    );
    yield put(
      ACTIONS.actionPreloadDateRangeAvailabilitySuccess(dateRangeAvailability)
    );
  } catch (e) {
    console.log(e);
    yield dispatchError(e.message);
    yield put(ACTIONS.actionPreloadDateRangeAvailabilityFail(e.message));
  }
}

export function* getSpecificVenueAvailabilityRequestHandler(data) {
  let venueIds = [];
  try {
    const input = { ...data.payload };
    const {
      venues,
      date,
      partySize,
      startTime = '06:00',
      endTime = '05:45',
    } = input;

    if (!venues || venues.length < 1) {
      yield put(ACTIONS.actionSetAvailabilityLoadingStatus(false));
      return;
    }

    venueIds = venues.map((venue) => venue.sevenRoomsVenueId);
    const chunkedPayloads = generateSpecificVenuesSearchRequestPayload({
      venueIds: venueIds,
      date,
      partySize,
      startTime,
      endTime,
    });

    // call payloads one after another, updating listAvailabilityProgress as each call loads
    for (let payload of chunkedPayloads) {
      const specificVenueAvailabilities = yield getSpecificVenueAvailabilities([
        payload,
      ]);
      if (specificVenueAvailabilities?.length > 0 && specificVenueAvailabilities[0]) {
        yield put(
          ACTIONS.actionListAvailabilityVenuesProgress(
            specificVenueAvailabilities.flat()
          )
        );
      }
    }
  } catch (e) {
    console.log(e.message);
    yield dispatchRedirectError(e.message);
    yield put(ACTIONS.actionGetSpecificVenueAvailabilityFail(venueIds));
  }
}

export function* getEventAvailabilityRequest(data) {
  try {
    const requestPayload = generateEventAvailabilityRequestPayload(
      data.payload
    );
    const listEventAvailabilities = yield getAvailabities(requestPayload);
    yield put(
      ACTIONS.actionGetEventAvailabilitySuccess(listEventAvailabilities)
    );
  } catch (e) {
    console.log(e);
    yield dispatchError(e.message);
    yield put(ACTIONS.actionGetEventAvailabilityFail);
  }
}

function* getListVenueAvailabities(payloads) {
  const results = yield all(
    payloads.map((payload) => {
      return callGraphqlWithToken({
        query: listAvailabilityVenues,
        variables: { input: payload },
      });
    })
  );
  return results.map((result) => result.data.listAvailabilityVenues);
}

export function* listAvailabilityVenuesRequest(data) {
  try {
    const input = { ...data.payload };
    const { page, venues, sevenRoomsVenueIds } = input;
    const newPayload = { ...input };
    let venuesPayload = [];
    let copyOfVenuesInput = [];

    if (input.sevenRoomsVenueIds) {
      copyOfVenuesInput = [...sevenRoomsVenueIds];
      venuesPayload = getVenuesByPage(copyOfVenuesInput, page);
      newPayload.sevenRoomsVenueIds = venuesPayload;
    } else {
      copyOfVenuesInput = [...venues];
      venuesPayload = getVenuesByPage(copyOfVenuesInput, page);
      newPayload.venues = venuesPayload;
    }

    if (!venuesPayload || venuesPayload.length < 1) {
      yield put(ACTIONS.actionSetAvailabilityLoadingStatus(false));
      return;
    }

    const today = moment();
    newPayload.currentDate = today.format('YYYY-MM-DD');
    newPayload.currentTime = today.format('HH:mm');
    delete newPayload.page;

    const generatedListSearchRequestPayloads =
      generateListSearchRequestPayload(newPayload);
    const listVenueAvailabilities = yield getListVenueAvailabities(
      generatedListSearchRequestPayloads
    );
    yield put(
      ACTIONS.actionListAvailabilityVenuesProgress(
        listVenueAvailabilities.map(
          (listVenueAvailability) => listVenueAvailability[0]
        )
      )
    );
    yield put(ACTIONS.actionSetAvailabilityLoadingStatus(false));
  } catch (e) {
    console.log(e);
    yield dispatchError(e.message);
    yield put(ACTIONS.actionListAvailabilityVenuesFail);
  }
}

export default function* availabilitysSaga() {
  yield takeLatest(
    TYPES.AVAILABILITY_LIST_REQUEST,
    availabilityListRequestHandler
  );
  yield takeLatest(
    TYPES.GET_AVAILABILITY_REQUEST,
    getAvailabilityRequestHandler
  );
  yield takeLatest(
    TYPES.GET_MULTIPLE_AVAILABILITIES_REQUEST,
    getMultipleAvailabilityRequestHandler
  );
  yield takeLatest(
    TYPES.FIND_AVAILABILITY_REQUEST,
    findAvailabilityRequestHandler
  );
  yield takeLatest(
    TYPES.LOAD_MORE_AVAILABILITIES,
    getAvailabilityRequestHandler
  );
  yield takeLatest(
    TYPES.GET_DATE_RANGE_AVAILABILITY_REQUEST,
    getDateRangeAvailabilityHandler
  );
  yield takeLatest(
    TYPES.PRELOAD_DATE_RANGE_AVAILABILITY_REQUEST,
    getPreloadDateRangeAvailabilityHandler
  );
  yield takeLatest(
    TYPES.GET_SPECIFIC_VENUE_AVAILABILITY_REQUEST,
    getSpecificVenueAvailabilityRequestHandler
  );
  yield takeLatest(
    TYPES.LIST_AVAILABILITY_VENUES_REQUEST,
    listAvailabilityVenuesRequest
  );
  yield takeEvery(
    TYPES.LOAD_DATE_AVAILABILITY_REQUEST,
    loadDateAvailabilityRequest
  );
  yield takeEvery(
    TYPES.GET_EVENT_AVAILABILITY_REQUEST,
    getEventAvailabilityRequest
  );
}
